import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Returns a stable function reference, like `useCallback`, but always calls the latest version of `fn`. This means that
 * the callback avoids stale closures, while also not changing on every render.
 *
 * Analogous to the `useEffectEvent` hook, but without the "effects only" call-site restriction.
 *
 * @param fn - The function to wrap.
 * @returns A stable callback that always calls the latest version of `fn`.
 */
export function useEventCallback<T extends (...args: any[]) => unknown>(fn: T): T {
	const ref = useRef<T>((() => {
		throw new Error('useLatestCallback: cannot invoke a callback during render.');
	}) as unknown as T);
	useLayoutEffect(() => {
		ref.current = fn;
	});
	return useCallback((...args: Parameters<T>) => ref.current(...args) as ReturnType<T>, []) as T;
}
